import React, {
    FocusEvent,
    FocusEventHandler,
    forwardRef,
    HTMLAttributes,
    memo,
    ReactNode,
    useCallback,
    useMemo,
    useState,
} from 'react';
import {
    Autocomplete,
    autocompleteClasses,
    AutocompleteProps,
    outlinedInputClasses,
} from '@mui/material';

import { DropdownOption } from '../Dropdown';
import { Stack } from '../Stack';
import { Typography } from '../Typography';
import { Search2 } from '../Search2';
import { MiniClose24, Search24 } from '../../icons';
import { useOpenState } from '../DropdownButton';


const useFocused = () => {
    const [isFocused, setFocus] = useState(false);
    const onFocus = useCallback(() => setFocus(true), []);
    const onBlur = useCallback(() => setFocus(false), []);
    return {
        isFocused,
        onFocus,
        onBlur,
    };
};

const getListBoxComponent = (
    helperText: string,
): AutocompleteProps<DropdownOption, false, false, false>['ListboxComponent'] =>
    forwardRef<any, HTMLAttributes<HTMLElement>>((props, ref) => (
        <Stack tabIndex={-1} spacing={0.5} p={2} sx={{ height: ({ spacing }) => spacing(58) }}>
            {helperText && (
                <Typography
                    variant='regularCaption1'
                    color='grayscale.shade85'
                    px={4}
                    pt={2}
                    tabIndex={-1}
                >
                    {helperText}
                </Typography>
            )}
            <ul ref={ref} {...props} />
        </Stack>
    ));

type AutocompleteSearchFilterProps = {
    value: DropdownOption;
    onChange: (value: DropdownOption) => void;
    options: DropdownOption[];
    inputValue: string;
    onInputChange: (value?: string) => void;
    openState: ReturnType<typeof useOpenState>;
    helperText?: string;
    placeholder?: string;
    onFocus?: FocusEventHandler<HTMLInputElement>;
    onBlur?: FocusEventHandler<HTMLInputElement>;
    startIcon?: ReactNode;
    label?: ReactNode;
    loading?: boolean;
    noOptionsText?: ReactNode;
    loadingText?: ReactNode;
    size?: 'large' | 'medium';
};


export const AutocompleteSearchFilter = memo<AutocompleteSearchFilterProps>(
    props => {
        const {
            value,
            onChange,
            inputValue,
            onInputChange,
            options,
            openState,
            helperText,
            placeholder,
            startIcon,
            label,
            loading = false,
            noOptionsText,
            loadingText,
            size = 'large',
        } = props;
        const { isFocused, onFocus, onBlur } = useFocused();

        return (
            <Autocomplete<DropdownOption, false, false, false>
                loading={loading}
                options={options}
                open={openState.isOpen}
                onChange={useCallback((_, v) => onChange(v), [onChange])}
                blurOnSelect
                clearIcon={value ? (
                    <MiniClose24
                        sx={({ palette }) => ({
                            fill: palette.grayscale.shade95,
                            fontSize: '24px',
                        })}
                    />
                ) : (
                    <Search24
                        sx={({ palette }) => ({
                            fill: palette.grayscale.shade85,
                            fontSize: '24px',
                        })}
                    />
                )}
                forcePopupIcon={false}
                value={value}
                filterOptions={o => o}
                inputValue={inputValue}
                onInputChange={useCallback(
                    (_, v) => onInputChange(v),
                    [onInputChange],
                )}
                autoHighlight
                isOptionEqualToValue={useCallback(
                    (o, v) => o.value === v.value,
                    [],
                )}
                ListboxComponent={useMemo(
                    () => getListBoxComponent(helperText),
                    [helperText],
                )}
                ListboxProps={{
                    style: {
                        maxHeight: '100%',
                    },
                }}
                componentsProps={{
                    clearIndicator: {
                        sx: {
                            visibility: 'visible',
                            '&:hover': {
                                backgroundColor: 'transparent',
                            },
                        },
                    },
                    popper: {
                        modifiers: [
                            {
                                name: 'offset',
                                options: {
                                    offset: [0, 8],
                                },
                            },
                        ],
                        sx: {
                            [`& .${autocompleteClasses.noOptions}`]: {
                                p: 0,
                            },
                            [`& .${autocompleteClasses.listbox}`]: {
                                p: 0,
                            },
                        },
                    },
                    paper: {
                        sx: {
                            boxShadow:
                                '0px 8px 24px rgba(0, 0, 0, 0.05), 0px 2px 8px rgba(0, 0, 0, 0.05)',
                            borderRadius: 3,
                        },
                    },
                }}
                sx={({ spacing }) => ({
                    [`& .${autocompleteClasses.clearIndicator}`]: {
                        p: 0,
                        mr: 0,
                    },
                    [`& .${autocompleteClasses.inputRoot} .${autocompleteClasses.endAdornment}`]: {
                        top: '50%',
                        bottom: 0,
                        right: spacing(4),
                        transform: 'translate(0, -50%)',
                    },
                    [`& .${outlinedInputClasses.root}`]: {
                        pt: size === 'large' ? 6 : 5,
                        pb: size === 'large' ? 2 : 1,
                        pl: 4,

                        [`& .${autocompleteClasses.input}`]: {
                            px: 0,
                            pb: 0,
                            pt: 0,
                            height: spacing(6),
                        },
                    },
                })}
                noOptionsText={noOptionsText}
                loadingText={loadingText}
                renderInput={({
                    size: inputSize,
                    InputProps: { startAdornment, ...InputProps },
                    ...props
                }) => (
                    <Search2
                        {...InputProps}
                        {...props}
                        isFocused={isFocused}
                        label={label}
                        isGray
                        startIcon={startIcon}
                        placeholder={placeholder}
                        value={props.inputProps.value}
                        onChange={props.inputProps.onChange}
                        onClick={openState.change}
                        onBlur={e => {
                            if (onBlur)
                                onBlur();
                            props.inputProps.onBlur(
                                e as FocusEvent<HTMLInputElement>,
                            );
                            openState.close();
                        }}
                        onFocus={e => {
                            if (onFocus)
                                onFocus();
                            props.inputProps.onFocus(
                                e as FocusEvent<HTMLInputElement>,
                            );
                        }}
                    />
                )}
                renderOption={({ className, ...props }, option) => (
                    <Stack
                        component='li'
                        borderRadius={2}
                        sx={{
                            p: 2,
                            cursor: 'pointer',
                            [`&:hover, & .${autocompleteClasses.focused}`]: {
                                bgcolor: 'grayscale.shade5',
                            },
                            [`& .${autocompleteClasses.focused}`]: {
                                bgcolor: 'grayscale.shade10',
                            },
                        }}
                        {...props}
                    >
                        <Typography
                            variant='regularCaption1'
                            color='grayscale.shade95'
                        >
                            {option.label}
                        </Typography>
                        {option.description && (
                            <Typography
                                variant='regularCaption1'
                                color='grayscale.shade85'
                            >
                                {option.description}
                            </Typography>
                        )}
                    </Stack>
                )}
            />
        );
    },
);
